import { FieldConfig } from "@/types/fieldConfig";
import { Chip, Button } from "@heroui/react";
import { Link } from "@tanstack/react-router";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import {
    faCheck,
    faCheckCircle,
    faCheckDouble,
    faCircleExclamation,
    faClipboard,
    faDrawPolygon,
    faFileZipper,
    faMicrochip,
    faTractor,
    faXmark,
} from "@fortawesome/free-solid-svg-icons";
import classNames from "classnames";
import ScenarioPlannerIcon from "@/components/nav/ScenarioPlannerIcon";
import dayjs from "dayjs";

type PlotStatusColor =
    | "primary"
    | "secondary"
    | "success"
    | "warning"
    | "danger";

export type PlotStatus = {
    text: string;
    color: PlotStatusColor;
    icon: IconDefinition;
    link: string;
};

// eslint-disable-next-line react-refresh/only-export-components
export function getPlotStatusChip(field: FieldConfig): PlotStatus {
    if (field.virtual_SI_mode) {
        return {
            text: "Virtual SI Mode",
            color: "secondary",
            icon: faMicrochip,
            link: "/dashboard/fields/$fieldId/insights/settings",
        };
    }

    if (field.plots_stale) {
        return {
            text: "Stale Plots",
            color: "danger",
            icon: faCircleExclamation,
            link: "/dashboard/fields/$fieldId/rx/new",
        };
    }

    if (field.plots_uploaded) {
        return {
            text: "Plots Uploaded",
            color: "success",
            icon: faCheckCircle,
            link: "/dashboard/fields/$fieldId/insights/settings",
        };
    }

    if (
        field.latest_plot_prescription_id &&
        field.latest_prescription_has_active_plots &&
        field.latest_prescription_application &&
        field.plots_confirmed
    ) {
        return {
            text: "Plots Confirmed",
            color: "success",
            icon: faCheckCircle,
            link: "/dashboard/fields/$fieldId/insights/settings",
        };
    }

    if (
        field.latest_plot_prescription_id &&
        field.latest_prescription_application &&
        !field.plots_confirmed
    ) {
        return {
            text: "Confirm Plots",
            color: "warning",
            icon: faCheckDouble,
            link: "/dashboard/fields/$fieldId/insights/settings",
        };
    }

    if (
        field.latest_plot_prescription_id &&
        field.latest_plot_prescription_exported &&
        !field.latest_prescription_application
    ) {
        return {
            text: "Log Application",
            color: "danger",
            icon: faClipboard,
            link: "/dashboard/fields/$fieldId/operations/add",
        };
    }

    if (
        field.latest_plot_prescription_id &&
        !field.latest_plot_prescription_exported
    ) {
        return {
            text: "Export Plot Rx",
            color: "danger",
            icon: faFileZipper,
            link: `/dashboard/fields/$fieldId/rx/${field.latest_plot_prescription_id}`,
        };
    }

    return {
        text: "Create Plot Rx",
        color: "danger",
        icon: faDrawPolygon,
        link: "/dashboard/fields/$fieldId/rx/new",
    };
}

export function PlotStatusCheckpoints({
    field,
    className,
}: {
    field: FieldConfig;
    className?: string;
}) {
    return (
        <div className={classNames("flex flex-row gap-1", className)}>
            <FontAwesomeIcon
                icon={faCheckCircle}
                className={
                    field.latest_plot_prescription_id
                        ? "text-green-500"
                        : "text-gray-300"
                }
                title="Prescription Created"
            />
            <FontAwesomeIcon
                icon={faCheckCircle}
                className={
                    field.latest_plot_prescription_exported
                        ? "text-green-500"
                        : "text-gray-300"
                }
                title="Prescription Exported"
            />
            <FontAwesomeIcon
                icon={faCheckCircle}
                className={
                    field.latest_prescription_application
                        ? "text-green-500"
                        : "text-gray-300"
                }
                title="Application Logged"
            />
            <FontAwesomeIcon
                icon={faCheckCircle}
                className={
                    field.plots_confirmed ? "text-green-500" : "text-gray-300"
                }
                title="Plots Confirmed"
            />
        </div>
    );
}

export function PlotStatusChip({ field }: { field: FieldConfig }) {
    const { text, color, icon, link } = getPlotStatusChip(field);

    return (
        <Chip
            as={Link}
            to={link}
            params={{
                // @ts-expect-error ts(2322)
                fieldId: field.id.toString(),
            }}
            target="_blank"
            color={color}
            endContent={<FontAwesomeIcon icon={icon} className="mr-2" />}
            variant="flat"
            className="text-xs cursor-pointer"
            radius="sm"
        >
            {text}
        </Chip>
    );
}

export function ManagementZoneChip({ field }: { field: FieldConfig }) {
    return (
        <Chip
            as={Link}
            to="/dashboard/fields/$fieldId/setup/management-zones"
            params={{
                // @ts-expect-error ts(2322)
                fieldId: field.id.toString(),
            }}
            target="_blank"
            color={field.has_primary_management_zones ? "secondary" : "default"}
            size="sm"
            radius="sm"
            startContent={
                <FontAwesomeIcon
                    icon={field.has_primary_management_zones ? faCheck : faXmark}
                    className="ml-2 mr-1"
                />
            }
        >
            {field.has_primary_management_zones ? "Yes" : "No"}
        </Chip>
    );
}

export function NPlanButton({
    field,
    onPress,
}: {
    field: FieldConfig;
    onPress: () => void;
}) {
    return (
        <div className="flex flex-row gap-1 items-center text-xs justify-center mx-2">
            {field.primary_application_plan_name ? (
                <div
                    className="text-secondary-800 rounded-md px-2 py-1 cursor-pointer hover:bg-secondary-200 bg-secondary-100"
                    onClick={onPress}
                >
                    {field.primary_application_plan_name}
                </div>
            ) : (
                <Button
                    color="primary"
                    variant="light"
                    size="sm"
                    radius="sm"
                    onPress={onPress}
                >
                    Planner
                    <ScenarioPlannerIcon color="primary" />
                </Button>
            )}
        </div>
    );
}

// eslint-disable-next-line react-refresh/only-export-components
export function isSoilIncomplete(field: FieldConfig): boolean {
    return (
        !field.soil_configuration ||
        !field.soil_organic_matter ||
        field.has_drainage === null ||
        !field.current_crop_cycle.cover_crop ||
        !field.current_crop_cycle.cover_crop_termination ||
        field.current_crop_cycle.manure_use === null ||
        field.current_crop_cycle.inhibitor_use === null ||
        field.current_crop_cycle.eef_use === null ||
        !field.current_crop_cycle.tillage_practice
    );
}

export function SoilManagementButton({
    field,
    onPress,
}: {
    field: FieldConfig;
    onPress: () => void;
}) {
    const color =
        field.soil_management_required && isSoilIncomplete(field)
            ? "danger"
            : "primary";

    return (
        <div className="flex flex-row gap-1 items-center text-xs justify-center mx-2">
            <Button
                color={color}
                variant="light"
                size="sm"
                radius="sm"
                onPress={onPress}
            >
                Soil & Management
                <FontAwesomeIcon icon={faTractor} />
            </Button>
        </div>
    );
}

// eslint-disable-next-line react-refresh/only-export-components
export function getPlantingSource(
    field: FieldConfig,
): "Logged" | "Default" | null {
    if (!field.active_planting_operation) return null;
    return field.active_planting_operation.id !== -1 ? "Logged" : "Default";
}

export function ActiveCropCell({
    cropName,
    plantingDate,
    plantingOperationId,
}: {
    cropName?: string | null;
    plantingDate?: string | null;
    /** null / undefined / -1 → Default; otherwise Logged */
    plantingOperationId?: number | null;
}) {
    if (!cropName && !plantingDate) {
        return null;
    }

    const source: "Logged" | "Default" =
        plantingOperationId != null && plantingOperationId !== -1
            ? "Logged"
            : "Default";

    return (
        <div className="flex flex-col items-start w-full text-sm pr-2">
            <div className="font-bold">{cropName}</div>
            <div className="flex flex-row items-center justify-between w-full">
                <span className="text-xs text-gray-500">
                    {plantingDate ? dayjs(plantingDate).format("ll") : null}
                </span>
                <Chip
                    color={source === "Logged" ? "secondary" : "default"}
                    size="sm"
                    classNames={{ base: "text-xs" }}
                    radius="sm"
                >
                    {source}
                </Chip>
            </div>
        </div>
    );
}

export function ConnectionsCell({ field }: { field: FieldConfig }) {
    const count = field.connections_list?.length ?? 0;

    return (
        <div
            className={classNames("text-xs text-gray-500", {
                "text-secondary-500": count > 0,
            })}
        >
            {count === 0 ? (
                "None"
            ) : (
                <>
                    <FontAwesomeIcon icon={faCheckCircle} className="mr-1" />
                    {count} connection{count > 1 ? "s" : ""}
                </>
            )}
        </div>
    );
}
